import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

// Our Components
import Dropdown from 'components/Dropdown/Dropdown';
import FinancialGoalDropdown from 'components/Dropdown/FinancialGoalDropdown';
import FinancialLiabilityForm from 'components/Forms/Liabilities/Common/FinancialLiabilityForm';
import FormButton from 'components/Forms/FormButton';
import LiabilityFormHeading from 'components/Forms/Liabilities/LiabilityFormHeading';
import LiabilityFormContainer from 'components/Forms/Liabilities/Common/LiabilityFormContainer';
import LiabilityFormItem from 'components/Forms/Liabilities/Common/LiabilityFormItem';
import Loader from 'components/Loader';
import { TEXT, NUMBER } from 'components/Input/Types';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import TextInput from 'components/Input/TextInput';

// Utils
import {
	dateToIsoFormat,
	FINANCIAL_GOAL_TO_ENUM_MAPPING,
	getDefaultApproxPayoffDate,
	isSubmissionReady,
	roundToDecimal
} from 'shared/utils';
import sentenceCapitalize from 'shared/utils/formatting/sentenceCapitalize';

// Our Hooks
import useGetUserStudentLiability from 'hooks/client/liabilities/queries/useGetUserStudentLiability';
import useSaveStudentLiability from 'hooks/client/liabilities/mutations/useMutateSaveStudentLiability';
import usePrepopulateState from 'hooks/prepopulate/usePrepopulateState';

// CONSTANTS
import { STUDENT } from 'shared/constants';

const DEGREE_TYPES = [
	'Some High School',
	'High School Graduate',
	'Bachelors',
	'Masters',
	'Doctorate',
	'Professional',
	'Other Educational Degree'
];
const TYPES_OF_STUDENT_LOANS = ['Federal', 'Private'];
const GRADUATION_YEAR_ONLY_VIEW = ['year']; // This is for the DatePicker of Graduation Year

function StudentLiability() {
	// Student Liability Related
	const navigate = useNavigate();
	const { isLoading, data, isSuccess } = useGetUserStudentLiability();
	const saveStudentLiability = useSaveStudentLiability();

	const { isLoading: isMutationLoading } = saveStudentLiability;

	const [approxPayoffDate, setApproxPayoffDate] = useState(
		getDefaultApproxPayoffDate(STUDENT)
	);
	const [lender, setLender] = useState('');
	const [lenderType, setLenderType] = useState('Federal');
	const [loanType, setLoanType] = useState('Fixed');
	const [monthlyPayment, setMonthlyPayment] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');
	const [outstandingBalance, setOutstandingBalance] = useState(''); // balance / 100 is bc methodfi provides balance in cents so we divide by 100 to convert into dollars
	const [rate, setRate] = useState('');

	// Student Related Data
	const [fieldOfStudy, setFieldOfStudy] = useState('');
	const [gpa, setGpa] = useState('');
	const [graduationYear, setGraduationYear] = useState(null);
	const [highestDegree, setHighestDegree] = useState('');
	const [schoolName, setSchoolName] = useState('');
	const [linkedinProfile, setLinkedinProfile] = useState('');

	// Financial Goal
	const [financialGoal, setFinancialGoal] = useState(
		'Reduce total cost of debt'
	);

	usePrepopulateState(
		isSuccess
			? [
					{
						setState: setOutstandingBalance,
						value: `${data?.outstandingBalance ?? ''}`
					},
					{ setState: setRate, value: `${data?.interestRate ?? ''}` },
					{
						setState: setApproxPayoffDate,
						value: data?.expectedPayOffDate
					},
					{ setState: setLender, value: data?.lender },
					{ setState: setMonthlyPayment, value: data?.monthlyPay },
					{
						setState: setOwnershipTenure,
						value: `${Math.floor(data?.tenureMonth ?? 0) / 12}`
					},
					{
						setState: setLenderType,
						value: `${sentenceCapitalize(data?.subType ?? '')}`
					},
					{
						setState: setLoanType,
						value: `${sentenceCapitalize(data?.loanType ?? '')}`
					},
					{ setState: setFinancialGoal, value: data?.financialGoal },
					{
						setState: setFieldOfStudy,
						value: data?.studentInfo?.fieldOfStudy
					},
					{ setState: setGpa, value: data?.studentInfo?.gpa },
					{
						setState: setGraduationYear,
						value: data?.studentInfo?.graduationYear
					},
					{
						setState: setLinkedinProfile,
						value: data?.studentInfo?.linkedin
					},
					{
						setState: setHighestDegree,
						value: data?.studentInfo?.degree
					},
					{
						setState: setSchoolName,
						value: data?.studentInfo?.school
					}
			  ]
			: [],
		[data]
	);

	const formValues = [
		approxPayoffDate,
		financialGoal,
		graduationYear,
		highestDegree,
		lender,
		lenderType,
		loanType,
		monthlyPayment,
		outstandingBalance,
		ownershipTenure,
		rate,
		schoolName
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(formValues) &&
			approxPayoffDate !== null &&
			graduationYear !== null,
		formValues
	);

	const handleApproxPayoffDateChange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setApproxPayoffDate('');
			return;
		}
		setApproxPayoffDate(value);
	};

	const handleGpachange = (value) => {
		const isValueEmpty = value === '';
		if (isValueEmpty) {
			setGpa(value);
			return;
		}
		const hasPointlessDecimal = value.includes('4.');
		if (hasPointlessDecimal) return;

		// if we made it here the value is not empty and its not 4.something
		const parsedValue = +value;
		const hasDecimal = parsedValue % 1 !== 0;

		if (parsedValue >= 1 && parsedValue <= 4) {
			if (hasDecimal) {
				const roundedValue = Math.floor(parsedValue * 100) / 100;

				setGpa(roundedValue);
				return;
			}

			setGpa(value);
		}
	};

	const submitLiabilityData = (route) => {
		const formattedApproxPayOffDate = dateToIsoFormat(approxPayoffDate);
		const formattedFinancialGoal =
			FINANCIAL_GOAL_TO_ENUM_MAPPING(financialGoal);
		const formattedGraduationYear = new Date(graduationYear).getFullYear(); // graduationYear is an object but this turns it into a number
		const formattedGPA = +gpa; // must be a num
		const formattedRate = roundToDecimal(+rate, 2);

		// everything below is subject to change once we databind.
		const studentLiability = {
			approxPayoffDate: formattedApproxPayOffDate,
			financialGoal: formattedFinancialGoal,
			fieldOfStudy,
			gpa: formattedGPA,
			graduationYear: formattedGraduationYear,
			degree: highestDegree,
			lender,
			lenderType: lenderType.toUpperCase(),
			linkedin: linkedinProfile,
			loanType: loanType.toUpperCase(),
			monthlyPayment: +monthlyPayment,
			outstandingBalance: +outstandingBalance,
			rate: formattedRate,
			school: schoolName,
			tenure: +ownershipTenure,
			tradelineType: STUDENT // can be one of AUTO | MORTGAGE | PERSONAL | STUDENT
		};

		saveStudentLiability.mutate(studentLiability, {
			onSuccess: () => {
				navigate(route);
			}
		});
	};

	if (isLoading)
		return (
			<>
				<Helmet>
					<title>Student Loan</title>
				</Helmet>
				<LiabilityFormHeading headingText="Student Loan" />
				<Loader
					size={60}
					boxSX={{ alignItems: 'center', marginTop: 15 }}
				/>
			</>
		);

	return (
		<>
			<Helmet>
				<title>Student Loan</title>
			</Helmet>

			{/* Once data binding begins we will add Mortgage specific information to this header */}
			<LiabilityFormHeading headingText="Student Loan" />

			<LiabilityFormContainer>
				<FinancialLiabilityForm
					outstandingBalance={outstandingBalance}
					setOutstandingBalance={setOutstandingBalance}
					monthlyPayment={monthlyPayment}
					setMonthlyPayment={setMonthlyPayment}
					rate={rate}
					setRate={setRate}
					approxPayoffDate={approxPayoffDate}
					handleApproxPayoffDateChange={handleApproxPayoffDateChange}
					ownershipTenure={ownershipTenure}
					setOwnershipTenure={setOwnershipTenure}
					loanType={loanType}
					setLoanType={setLoanType}
					lender={lender}
					setLender={setLender}
				/>

				<LiabilityFormItem>
					<Dropdown
						dataTestTag="loanType"
						items={TYPES_OF_STUDENT_LOANS}
						selected={lenderType}
						onChange={setLenderType}
						variant="outlined"
						label="Lender Type"
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<Dropdown
						items={DEGREE_TYPES}
						label="Degree"
						selected={highestDegree}
						onChange={setHighestDegree}
						helperText="Degree obtained."
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<TextInput
						inputProps={{
							'data-test': 'school'
						}}
						type={TEXT}
						label="School attended"
						value={schoolName}
						onChange={setSchoolName}
						subLabel="Name of university"
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<TextInput
						type={TEXT}
						label="What was your field of study?"
						value={fieldOfStudy}
						onChange={setFieldOfStudy}
						subLabel="Optional"
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<TextInput
						type={NUMBER}
						label="What was your GPA?"
						value={gpa}
						onChange={handleGpachange}
						subLabel="Optional. Please report out of 4.0 scale."
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<StandardDatePicker
						helperText="Enter a year like 2022"
						inputFormat="yyyy"
						label="Graduation year"
						onChange={setGraduationYear}
						variant="outlined"
						value={graduationYear}
						views={GRADUATION_YEAR_ONLY_VIEW}
					/>
				</LiabilityFormItem>

				<LiabilityFormItem>
					<TextInput
						type={TEXT}
						label="Linkedin profile"
						value={linkedinProfile}
						onChange={setLinkedinProfile}
						subLabel="Optional"
					/>
				</LiabilityFormItem>

				<FinancialGoalDropdown
					withoutCashout
					financialGoal={financialGoal}
					setFinancialGoal={setFinancialGoal}
				/>

				<FormButton
					isFormReady={isFormReady}
					isMutationLoading={isMutationLoading}
					submitLiabilityData={submitLiabilityData}
				/>
			</LiabilityFormContainer>
		</>
	);
}

export default StudentLiability;
